import React from 'react';
import { PromptVariable } from '../types';
import { VARIABLE_PATTERN } from '../utils/variables';
import MarkdownContent from './MarkdownContent';

interface HighlightedPromptProps {
  text: string;
  variables?: PromptVariable[];
  className?: string;
  /** When true, non-variable segments render as markdown (detail view). */
  markdown?: boolean;
}

function variableLookup(
  variables: PromptVariable[] | undefined,
  name: string
): PromptVariable | undefined {
  return variables?.find((v) => v.name === name);
}

function VariableToken({
  full,
  name,
  meta,
}: {
  full: string;
  name: string;
  meta?: PromptVariable;
}) {
  const defined = Boolean(meta);
  const tooltip = meta
    ? [
        meta.name,
        meta.type && `type: ${meta.type}`,
        meta.required ? 'required' : 'optional',
        meta.description,
        meta.default !== undefined ? `default: ${String(meta.default)}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : `Undefined variable: ${name}`;

  return (
    <span
      title={tooltip}
      className={
        defined
          ? 'text-blue-700 font-semibold bg-blue-50 rounded px-0.5'
          : 'text-amber-800 font-semibold bg-amber-100 rounded px-0.5 underline decoration-amber-500 decoration-wavy'
      }
    >
      {full}
    </span>
  );
}

/**
 * Renders prompt text with ${Variable} tokens highlighted.
 * Defined variables use blue; undefined use amber warning styling.
 */
export const HighlightedPrompt: React.FC<HighlightedPromptProps> = ({
  text,
  variables,
  className = '',
  markdown = false,
}) => {
  if (!text) {
    return markdown ? <div className={className} /> : <pre className={className} />;
  }

  const parts: React.ReactNode[] = [];
  const regex = new RegExp(VARIABLE_PATTERN.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const chunk = text.slice(lastIndex, match.index);
      parts.push(
        markdown ? (
          <MarkdownContent key={`t-${key++}`} preserveLineBreaks>
            {chunk}
          </MarkdownContent>
        ) : (
          <span key={`t-${key++}`}>{chunk}</span>
        )
      );
    }

    const full = match[0];
    const name = match[1];
    parts.push(
      <VariableToken
        key={`v-${key++}`}
        full={full}
        name={name}
        meta={variableLookup(variables, name)}
      />
    );

    lastIndex = match.index + full.length;
  }

  if (lastIndex < text.length) {
    const chunk = text.slice(lastIndex);
    parts.push(
      markdown ? (
        <MarkdownContent key={`t-${key++}`} preserveLineBreaks>
          {chunk}
        </MarkdownContent>
      ) : (
        <span key={`t-${key++}`}>{chunk}</span>
      )
    );
  }

  if (markdown) {
    return <div className={className}>{parts}</div>;
  }

  return (
    <pre className={`whitespace-pre-wrap break-words ${className}`}>{parts}</pre>
  );
};

export default HighlightedPrompt;
